import { action } from '@reatom/framework'
import * as DateF from 'date-fns'

import { Result } from '@blade/std-lib'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'

import type { SupportTicketId } from '../model/support-ticket-id'
import type {
  SupportTicketsCreatePayload,
  SupportTicketsListParams,
  SupportTicketsMessagesCreatePayload,
  SupportTicketsMessagesListParams,
  SupportTicketsStatusPartialUpdatePayload,
} from '@blade/trade-api-client'

export const createSupportTicketRequestAction = action(
  async (ctx, req: SupportTicketsCreatePayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.support.supportTicketsCreate, [req]),
    )

    return result
  },
  'createSupportTicketRequestAction',
)

export const fetchSupportTicketsRequestAction = action(
  async (ctx, req: SupportTicketsListParams) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.support.supportTicketsList, [req]),
    )

    return result
  },
  'fetchSupportTicketsRequestAction',
)

// @deprecated replaced by reatomSupportTickets.getTicketById
export const fetchSupportTicketByIdRequestAction = action(
  async (ctx, ticketId: SupportTicketId) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.support.supportTicketsDetail, [
        {
          ticketId,
        },
      ]),
    )

    return result
  },
  'fetchSupportTicketByIdRequestAction',
)

export const fetchSupportTicketMessagesRequestAction = action(
  async (ctx, req: SupportTicketsMessagesListParams) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      ctx.schedule(() =>
        apiRequest(ctx, tradeApiClient.support.supportTicketsMessagesList, [
          req,
        ]),
      ),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'fetchSupportTicketMessagesRequestAction',
)

export const sendSupportTicketMessageRequestAction = action(
  async (
    ctx,
    {
      ticketId,
      ...req
    }: { ticketId: SupportTicketId } & SupportTicketsMessagesCreatePayload,
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.support.supportTicketsMessagesCreate, [
        { ticketId },
        req,
      ]),
    )

    return result
  },
  'sendSupportTicketMessageRequestAction',
)

// @deprecated not used by a user, no UI for this action
export const updateSupportTicketStatusRequestAction = action(
  async (
    ctx,
    {
      ticketId,
      ...req
    }: { ticketId: SupportTicketId } & SupportTicketsStatusPartialUpdatePayload,
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.support.supportTicketsStatusPartialUpdate,
        [{ ticketId }, req],
      ),
    )

    return result
  },
  'updateSupportTicketStatusRequestAction',
)

export type SupportWsToken = {
  token: string
  expiresAt: Date
  expiresInSec: number
}

export const createSupportWsTokenRequestAction = action(async (ctx) => {
  const { tradeApiClient } = ctx.get(dependenciesAtom)

  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.support.supportWsTicketCreate, []),
  )

  return result.pipe(
    Result.mapError(ApiError.fallbackToUnknown),
    Result.map(
      (it): SupportWsToken => ({
        token: it.wsTicket,
        expiresAt: DateF.addSeconds(new Date(), it.expiresInSec),
        expiresInSec: it.expiresInSec,
      }),
    ),
  )
}, 'createSupportWsTokenRequestAction')
